import { c as _c } from "react/compiler-runtime";
import { darken, useTheme } from "@mui/material";
import type { GetServerSideProps, NextPage } from "next";
import React from "react";
import { ConfigContext } from "#/context";
import { MainAppBar } from "#/features/appBar/ui/MainAppBar";
import { CodePanel } from "#/features/codeRunner/ui/CodePanel";
import { OutputPanel } from "#/features/output/ui/OutputPanel";
import { PlaygroundViewProvider } from "#/features/playground/context/PlaygroundViewContext";
import { MobilePlayground } from "#/features/playground/ui/MobilePlayground";
import { ProjectPanel } from "#/features/project/ui/ProjectPanel";
import { TreeViewPanel } from "#/features/treeViewer/ui/TreeViewPanel";
import { db } from "#/server/db/client";
import { useAppConfig, useHasMounted } from "#/shared/hooks";
import { useMobileLayout } from "#/shared/hooks/useMobileLayout";
import { absoluteUrlFromPathname, DEFAULT_SITE_DESCRIPTION, pathnameFromResolvedUrl } from "#/shared/lib/seo";
import { resolveSsrDeviceType, setDeviceHintResponseHeaders } from "#/shared/lib/ssrDevice";
import { SiteSeoHead } from "#/shared/ui/seo/SiteSeoHead";
import { PageScrollContainer } from "#/shared/ui/templates/PageScrollContainer";
import type { SplitPanelsLayoutProps } from "#/shared/ui/templates/SplitPanelsLayout/SplitPanelsLayout";
import { SplitPanelsLayout } from "#/shared/ui/templates/SplitPanelsLayout/SplitPanelsLayout";
import { SplitPanelsLayoutSkeleton } from "#/shared/ui/templates/SplitPanelsLayout/SplitPanelsLayoutSkeleton";
import type { SsrDeviceType } from "#/themes";
type DesktopWrapperProps = SplitPanelsLayoutProps;
const DesktopWrapper: React.FC<DesktopWrapperProps> = t0 => {
  const $ = _c(6);
  const {
    TopLeft,
    BottomLeft,
    TopRight,
    BottomRight
  } = t0;
  const hasMounted = useHasMounted();
  if (!hasMounted) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <SplitPanelsLayoutSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== BottomLeft || $[2] !== BottomRight || $[3] !== TopLeft || $[4] !== TopRight) {
    t1 = <SplitPanelsLayout component="main" TopLeft={TopLeft} BottomLeft={BottomLeft} TopRight={TopRight} BottomRight={BottomRight} />;
    $[1] = BottomLeft;
    $[2] = BottomRight;
    $[3] = TopLeft;
    $[4] = TopRight;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
type PlaygroundPageProps = {
  ssrDeviceType: SsrDeviceType;
  canonicalUrl: string;
  pageTitle: string;
  pageDescription: string;
};
const PlaygroundPage: NextPage<PlaygroundPageProps> = t0 => {
  const $ = _c(22);
  const {
    canonicalUrl,
    pageTitle,
    pageDescription
  } = t0;
  const theme = useTheme();
  const isMobile = useMobileLayout();
  const {
    data: t1
  } = useAppConfig();
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const data = t2;
  let t3;
  if ($[2] !== canonicalUrl || $[3] !== pageDescription || $[4] !== pageTitle) {
    t3 = <SiteSeoHead title={pageTitle} description={pageDescription} canonicalUrl={canonicalUrl} />;
    $[2] = canonicalUrl;
    $[3] = pageDescription;
    $[4] = pageTitle;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isMobile) {
    t4 = isMobile ? {
      overflow: {
        x: "hidden",
        y: "hidden"
      }
    } : {
      scrollbars: {
        autoHide: "scroll"
      },
      overflow: {
        x: "hidden"
      }
    };
    $[6] = isMobile;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== theme.palette.background.default) {
    t5 = darken(theme.palette.background.default, 0.1);
    $[8] = theme.palette.background.default;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = {
      height: "100vh",
      background: t5
    };
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isMobile) {
    t7 = isMobile ? <PlaygroundViewProvider><MainAppBar toolbarVariant="dense" /><MobilePlayground /></PlaygroundViewProvider> : <><MainAppBar toolbarVariant="dense" /><DesktopWrapper TopLeft={ProjectPanel} BottomLeft={CodePanel} TopRight={TreeViewPanel} BottomRight={OutputPanel} /></>;
    $[12] = isMobile;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t4 || $[15] !== t6 || $[16] !== t7) {
    t8 = <PageScrollContainer isPage={true} options={t4} style={t6}>{t7}</PageScrollContainer>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== data || $[19] !== t3 || $[20] !== t8) {
    t9 = <ConfigContext.Provider value={data}>{t3}{t8}</ConfigContext.Provider>;
    $[18] = data;
    $[19] = t3;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};
export const getServerSideProps: GetServerSideProps<PlaygroundPageProps> = async ({
  req,
  res,
  params,
  resolvedUrl
}) => {
  const ssrDeviceType = resolveSsrDeviceType(req.headers);
  setDeviceHintResponseHeaders(res);
  const slug = params?.slug;
  const slugStr = Array.isArray(slug) ? slug[0] : undefined;
  const pathOnly = pathnameFromResolvedUrl(resolvedUrl) || "/playground";
  const canonicalUrl = absoluteUrlFromPathname(pathOnly);
  let pageTitle = "Playground | dStruct";
  let pageDescription = "Write code in the dStruct playground and visualize data structures for LeetCode-style problems.";
  if (slugStr) {
    const project = await db.playgroundProject.findUnique({
      where: {
        slug: slugStr
      },
      select: {
        title: true,
        description: true
      }
    });
    if (project) {
      pageTitle = `${project.title} | dStruct`;
      pageDescription = project.description?.trim() ? `${project.title}: ${project.description.trim()}` : `Practice ${project.title} in dStruct — visualize solutions and run code in the browser.`;
    }
  } else {
    pageDescription = DEFAULT_SITE_DESCRIPTION;
  }
  return {
    props: {
      ssrDeviceType,
      canonicalUrl,
      pageTitle,
      pageDescription
    }
  };
};
export default PlaygroundPage;